import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { getAnonymousGateDecision } from '#/auth/authGate'
import type { AuthStatus } from '#/auth/authStatus'
import { getUrlForNextRoute, goToPage } from '#/auth/nextUrl'
import { useAuthStatus } from '#/auth/useAuthStatus'
import { useNextRoute } from '#/auth/useNextRoute'
import LoadingSpinner from '#/components/common/loadingSpinner'

export interface RequireAnonymousProps {
  children: React.ReactNode
}

/**
 * The mirror of `RequireAuth` for the screens a session makes pointless.
 *
 * Decides on the reading it arrived with and then stays out of the way: a session appearing underneath one of these
 * screens is `AuthChangeRedirector`'s business, and swapping the card for a spinner would hide whatever the screen is
 * saying about the sign-in it just made.
 */
export default function RequireAnonymous({ children }: RequireAnonymousProps) {
  const { data: authStatus } = useAuthStatus()
  const nextRoute = useNextRoute()
  const { pathname } = useLocation()

  // Latched per route rather than per mount: every anonymous screen renders this gate in the same slot, so React keeps
  // one instance across a move between them. A reading held over that move would show the login form to somebody sign
  // up had just signed in. Stays `undefined` until allauth answers, which this gate does not wait for.
  const arrival = useRef<{ pathname: string; status?: AuthStatus }>({ pathname })
  if (arrival.current.pathname !== pathname) {
    arrival.current = { pathname }
  }
  arrival.current.status ??= authStatus

  const decision = getAnonymousGateDecision({ authStatus: arrival.current.status })

  useEffect(() => {
    if (decision !== 'redirect') {
      return
    }
    goToPage(getUrlForNextRoute(nextRoute))
  }, [decision, nextRoute])

  // The spinner covers the gap between deciding to leave and the page load starting
  return decision === 'allow' ? <>{children}</> : <LoadingSpinner />
}
